/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { DateTime } from 'luxon'
import { useContext, useMemo } from 'react'
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from 'react-intl'
import ButtonGroup from '../../../../client/js/components/form/ButtonGroup'
import FormField from '../../../../client/js/components/form/fields/FormField'
import FormSaveButton from '../../../../client/js/components/form/buttons/FormSaveButton'
import SmartForm from '../../../../client/js/components/form/SmartForm'
import RecordMetas from '../../../../client/js/components/RecordMetas'
import {
  createFieldInitializer,
  createFieldValidator,
  createFormValidator,
  removeUnknownFields
} from '../../../../client/js/lib/form'
import { AuthContext } from '../../../auth/components/AuthProvider'
import {
  findTransactionsByIntervention,
  patchIntervention,
  postIntervention
} from '../../lib/rest/interventionAPIClient'
import { fetchLease, fetchLessorByEstateIdAndDate } from '../../lib/rest/leaseAPIClient'
import InterventionSchema from '../../schemas/InterventionSchema.mjs'
import AssigneeField from '../fields/AssigneeField'
import DescriptionField from '../fields/DescriptionField'
import EstateField from '../fields/EstateField'
import InterventionTypeField from '../fields/InterventionTypeField'
import LeaseField from '../fields/LeaseField'
import NoteField from '../fields/NoteField'
import PriorityField from '../fields/PriorityField'
import SubjectField from '../fields/SubjectField'
import FormSection from '../FormSection'
import StartDateField from '../fields/StartDateField'
import EndDateField from '../fields/EndDateField'
import { parseBoolean } from '@jalik/form-parser'
import { queryClient } from '../../../../client/js/lib/query'
import { getInterventionListLink } from '../../lib/links/interventionLinks'
import FormResetButton from '../../../../client/js/components/form/buttons/FormResetButton'
import {
  Alert,
  Anchor,
  Box,
  Button,
  Loader,
  SimpleGrid,
  Stack,
  Switch,
  Table,
  Text
} from '@mantine/core'
import CustomerLink from '../customer/CustomerLink'
import { useQuery } from '@tanstack/react-query'
import FormDateTimeField from '../../../../client/js/components/form/fields/FormDatetimeField'
import useCustomForm from '../../../../client/js/hooks/useCustomForm'
import { getTransactionCreateLink, getTransactionLink } from '../../lib/links/transactionLinks'
import { Link } from 'react-router-dom'
import { CATEGORY_CHARGE } from '../../enums/TransactionCategories.mjs'

export type InterventionFormProps = {
  initialValues: Partial<Intervention>,
  redirectUrl?: string
}

function InterventionForm (props: InterventionFormProps) {
  const {
    initialValues,
    redirectUrl = getInterventionListLink()
  } = props

  const intl = useIntl()
  const auth = useContext(AuthContext)
  const id = initialValues ? initialValues.id : null
  const editable = auth.hasPermission('write:intervention')

  const form = useCustomForm({
    disabled: !editable,
    initialValues: useMemo<Partial<Intervention>>(() => ({
      priority: 0,
      requestedAt: new Date().toISOString(),
      ...initialValues
    }), [initialValues]),
    initializeField: createFieldInitializer(InterventionSchema),
    validate: createFormValidator(InterventionSchema),
    validateField: createFieldValidator(InterventionSchema),
    onSubmit: (submitValues) => {
      const finalValues = removeUnknownFields(submitValues, InterventionSchema)
      return id > 0
        ? patchIntervention(id, finalValues)
        : postIntervention(finalValues)
    },
    onSuccess () {
      queryClient.invalidateQueries({ queryKey: ['intervention', id] })
      queryClient.invalidateQueries({ queryKey: ['interventions'] })
    }
  })
  const { values } = form

  const leaseFilter = useMemo(() => ({
    estateId: values.estateId
  }), [values.estateId])

  const lessorQuery = useQuery({
    enabled: values.estateId != null && values.requestedAt != null,
    queryKey: ['lessorByEstateAndDate', values.estateId, values.requestedAt],
    queryFn: () => fetchLessorByEstateIdAndDate(values.estateId, values.requestedAt)
  })
  const { data: lessor } = lessorQuery

  const leaseQuery = useQuery({
    enabled: values.leaseId != null,
    queryKey: ['lease', values.leaseId],
    queryFn: () => fetchLease(values.leaseId)
  })
  const { data: lease } = leaseQuery
  const tenant = lease?.Tenant

  const transactionsQuery = useQuery({
    enabled: values.id != null,
    queryKey: ['transactionsByIntervention', values.estateId, values.requestedAt],
    queryFn: () => findTransactionsByIntervention(values.id)
  })
  const transactions = transactionsQuery.data?._embedded?.transactions ?? []

  return (
    <SmartForm
      context={form}
      redirectUrl={id == null ? redirectUrl : undefined}
    >
      <Stack>
        {initialValues
          ? <RecordMetas metas={initialValues} />
          : null}

        <SimpleGrid cols={{ lg: 2 }}>
          <FormSection
            defaultOpened
            title={intl.formatMessage({
              id: 'general',
              defaultMessage: 'Général'
            })}
          >
            <EstateField disabled={!!initialValues?.estateId} />

            <LeaseField
              disabled={!values.estateId || !!initialValues?.leaseId}
              queryFilter={leaseFilter}
            />

            <SimpleGrid cols={{ lg: 2 }}>
              <Box>
                <Text c="gray">
                  <FormattedMessage id="owner" />
                </Text>
                {lessor
                  ? <CustomerLink customer={lessor} />
                  : null}
              </Box>
              <Box>
                <Text c="gray">
                  <FormattedMessage id="tenant" />
                </Text>
                {tenant
                  ? <CustomerLink customer={tenant} />
                  : '-'}
              </Box>
            </SimpleGrid>

            <FormDateTimeField
              label={intl.formatMessage({
                id: 'intervention_request_date',
                defaultMessage: 'Date de la demande d\'intervention'
              })}
              name="requestedAt"
              description={
                <FormattedMessage
                  id="intervention_request_date_help"
                  defaultMessage="Indiquez la date à laquelle l'intervention a été demandée par le locataire ou le propriétaire du bien."
                />}
            />

            <SimpleGrid cols={{ lg: 2 }}>
              <InterventionTypeField />
              <PriorityField />
            </SimpleGrid>

            <SubjectField />
            <DescriptionField rows={12} />
          </FormSection>

          <Box>
            <FormSection
              defaultOpened
              title={intl.formatMessage({
                id: 'planification',
                defaultMessage: 'Planification'
              })}
            >
              <AssigneeField
                description={<FormattedMessage
                  id="intervention_assignee_help"
                  defaultMessage="Indiquez la personne ou la société assignée pour l'intervention."
                />}
              />

              <SimpleGrid cols={{ xl: 2 }}>
                <FormDateTimeField
                  label={intl.formatMessage({
                    id: 'intervention_assignee_call_date',
                    defaultMessage: 'Responsable prévenu par téléphone'
                  })}
                  name="assigneeCalledAt"
                />
                <FormDateTimeField
                  label={intl.formatMessage({
                    id: 'intervention_assignee_email_date',
                    defaultMessage: 'Responsable prévenu par e-mail'
                  })}
                  name="assigneeEmailedAt"
                />
              </SimpleGrid>
              <SimpleGrid cols={{ xl: 2 }}>
                <StartDateField
                  label={intl.formatMessage({
                    id: 'intervention_start_date',
                    defaultMessage: 'Début de l\'intervention'
                  })}
                />
                <EndDateField
                  label={intl.formatMessage({
                    id: 'intervention_end_date',
                    defaultMessage: 'Fin de l\'intervention'
                  })}
                />
              </SimpleGrid>

              <NoteField
                disabled={!values.endDate && !values.noInvoice}
                label={intl.formatMessage({
                  id: 'remarks',
                  defaultMessage: 'Remarques'
                })}
                rows={5}
              />
            </FormSection>

            <FormSection
              defaultOpened
              title={intl.formatMessage({ id: 'invoicing' })}
            >
              {values.endDate == null &&
              values.startDate != null &&
              DateTime.fromISO(values.startDate).toMillis() <= Date.now()
                ? <Alert color="yellow">
                  {intl.formatMessage({
                    id: 'intervention_ongoing_alert',
                    defaultMessage: 'Veuillez saisir la date de fin de l\'intervention ou cocher la case ci-dessous si l\'intervention n\'a donné lieu à aucune facture.'
                  })}
                </Alert>
                : null}

              <SimpleGrid cols={{ lg: 2 }}>
                <FormField
                  disabled={transactions.length > 0}
                  label={intl.formatMessage({
                    id: 'intervention_without_invoice',
                    defaultMessage: 'Pas de facture'
                  })}
                  name="noInvoice"
                  parser={parseBoolean}
                  render={Switch}
                  type="checkbox"
                  value="true"
                />
                <Button
                  disabled={values.startDate == null || values.endDate == null || id == null || values.noInvoice}
                  component={Link}
                  target="_blank"
                  to={getTransactionCreateLink(true, {
                    customerId: lessor?.id,
                    estateId: values.estateId,
                    leaseId: values.leaseId,
                    interventionId: id,
                    category: CATEGORY_CHARGE
                  })}
                  variant="default"
                >
                  <FormattedMessage
                    id="create_expense"
                    defaultMessage="Créer une dépense"
                  />
                </Button>
              </SimpleGrid>

              {transactionsQuery.isFetching
                ? <Loader />
                : <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>
                        <FormattedMessage id="label" />
                      </Table.Th>
                      <Table.Th>
                        <FormattedMessage id="amount_excluded_tax" />
                      </Table.Th>
                      <Table.Th>
                        TVA
                      </Table.Th>
                      <Table.Th>
                        CPS
                      </Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {transactions.map((el) => (
                      <Table.Tr key={el.id}>
                        <Table.Td>
                          <Anchor
                            component={Link}
                            to={getTransactionLink(el.id, el.expense)}
                            target="_blank"
                            inherit
                          >
                            {el.label}
                          </Anchor>
                          <Text inherit c="gray">
                            <FormattedDate value={el.dueAt} />
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <FormattedNumber value={el.amount} />
                        </Table.Td>
                        <Table.Td>
                          <FormattedNumber value={el.vat} />
                        </Table.Td>
                        <Table.Td>
                          <FormattedNumber value={el.cps} />
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>}
            </FormSection>
          </Box>

        </SimpleGrid>

        {editable
          ? <ButtonGroup>
            <FormResetButton />
            <FormSaveButton />
          </ButtonGroup>
          : null}
      </Stack>
    </SmartForm>
  )
}

export default InterventionForm
